import { UseApiData } from "../../common/hooks/api";
import { Api } from "../../common/service/Api";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
} from "../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationPublicId, UUIDString } from "../../common/service/server/Types";
import { QueryKey, useMutation, UseMutationResult, useQueryClient } from "@tanstack/react-query";
import { Operation } from "fast-json-patch";
import { useApiSnackbar } from "../../common/service/resources/mutators";
import { useMemo } from "react";
import { createOrganizationVendorsQueryKey } from "./useOrganizationVendors";
import { useDataRequest } from "../../common/hooks/api";

export const createDataRecipientsQueryKey = (
  organizationId: OrganizationPublicId,
  options?: FetchDataRecipientsOptions,
): QueryKey => ["dataRecipients", organizationId, { ...options }];

export type UseDataRecipients = UseApiData<OrganizationDataRecipientDto[]> & {
  mutators: {
    patch: DataRecipientPatchMutation;
  };
};

export type FetchDataRecipientsOptions = {
  excludeRecipientCategories?: String[] | undefined;
  includeDataStorageLocations?: boolean | undefined;
  excludeIncomplete?: boolean | undefined;
  collectionContext?: CollectionContext[] | undefined;
};

export type DataRecipientsPatchResult = {
  dataRecipientResults: OrganizationDataRecipientDto[];
  success: boolean;
  failedVendorIds: UUIDString[];
};

export const useDataRecipients = (
  organizationId: OrganizationPublicId,
  options?: FetchDataRecipientsOptions,
): UseDataRecipients => {
  const fetchDataRecipients = useDataRequest({
    queryKey: createDataRecipientsQueryKey(organizationId, options),
    api: () =>
      Api.dataRecipient.getDataRecipients(
        organizationId,
        options?.excludeRecipientCategories,
        options?.includeDataStorageLocations,
        options?.excludeIncomplete,
      ),
    staleTime: 300,
    invalidation: {
      wait: 700,
      trailing: true,
    },
  });

  const patchDataRecipient = usePatchDataRecipient(organizationId);

  return {
    data: fetchDataRecipients.result ?? [],
    remote: fetchDataRecipients.result ?? [],
    request: fetchDataRecipients.request,
    refresh: fetchDataRecipients.refresh,
    invalidate: fetchDataRecipients.invalidate,
    mutators: {
      patch: patchDataRecipient,
    },
  };
};

export type PatchDataRecipientParams = {
  vendorId: UUIDString;
  operations: Operation[];
};

type PatchDataRecipientContext = { previous: OrganizationDataRecipientDto };

export type DataRecipientPatchMutation = UseMutationResult<
  OrganizationDataRecipientDto,
  unknown,
  PatchDataRecipientParams,
  PatchDataRecipientContext
>;

export const usePatchDataRecipient = (organizationId: UUIDString): DataRecipientPatchMutation => {
  const queryClient = useQueryClient();
  const { apiError } = useApiSnackbar();

  const dataRecipientsQueryKey = createDataRecipientsQueryKey(organizationId);

  // NOTE: for now, maintain the sets of query keys for both useDataRecipients and useOrganizationVendors hooks
  // TODO: remove this extra maintenance logic once the useOrganizationVendors hook has been fully deprecated
  const organizationVendorsQueryKey = createOrganizationVendorsQueryKey(organizationId);

  const isMatch = useMemo(
    () => (queryKey: QueryKey) =>
      (queryKey[0] == dataRecipientsQueryKey[0] && queryKey[1] == dataRecipientsQueryKey[1]) ||
      (queryKey[0] == organizationVendorsQueryKey[0] &&
        queryKey[1] == organizationVendorsQueryKey[1]),
    [dataRecipientsQueryKey, organizationVendorsQueryKey],
  );

  const updateCache = useMemo(
    () => (queryKey: QueryKey, data: OrganizationDataRecipientDto) => {
      // console.log(`updating query cache: `, queryKey);
      const cache = queryClient.getQueryData<OrganizationDataRecipientDto[]>(queryKey) ?? [];
      const newCache = cache.map((item) => (item.id === data.id ? data : { ...item }));
      if (!newCache.includes(data)) {
        newCache.push(data);
      }
      queryClient.setQueryData(queryKey, newCache);
    },
    [queryClient],
  );

  const mutation = useMutation({
    mutationKey: [...dataRecipientsQueryKey, "patch"],
    mutationFn: async (params: PatchDataRecipientParams) => {
      const { vendorId, operations } = params;
      return await Api.dataRecipient.patchDataRecipient(organizationId, vendorId, operations);
    },
    onSuccess: async (data: OrganizationDataRecipientDto) => {
      queryClient
        .getQueryCache()
        .findAll({
          predicate: (query) => isMatch(query.queryKey) && !query.isStale(),
        })
        .forEach((query) => updateCache(query.queryKey, data));
      await queryClient.invalidateQueries({
        predicate: (query) => isMatch(query.queryKey) && query.isActive(),
      });
    },
    onError: (_error) => {
      console.error(_error);
      apiError("Something went wrong while we were updating the Data Recipient.");
    },
  });

  //@ts-ignore
  return mutation;
};

export const usePatchDataRecipients = (
  organizationId: UUIDString,
): UseMutationResult<
  DataRecipientsPatchResult,
  Error,
  { vendorIds: UUIDString[]; operations: Operation[] }
> => {
  const queryClient = useQueryClient();
  const { apiError } = useApiSnackbar();
  const dataRecipientsQueryKey = createDataRecipientsQueryKey(organizationId);

  return useMutation({
    mutationKey: [...dataRecipientsQueryKey, "bulk-patch"],
    mutationFn: async (params) => {
      return await Api.dataRecipient.patchDataRecipients(organizationId, params);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: dataRecipientsQueryKey });
    },
    onError: (_error) => {
      console.error(_error);
      apiError("Something went wrong while updating the Data Recipients.");
    },
  });
};
